import { useState, useMemo } from "react";

import { BaseDropdown } from "@components/library/UI/BaseDropdown";

import { ReactComponent as StarIcon } from "@assets/images/icons/star.svg";
import { ReactComponent as ArrowDownRed } from "@assets/images/icons/arrow-down-red.svg";
import { ReactComponent as ArrowUpGreen } from "@assets/images/icons/arrow-up-green.svg";
import { ReactComponent as ArrowIcon } from "@assets/images/icons/arrow-down-icon.svg";
import { ReactComponent as ArrowDownIcon } from "@assets/images/icons/arrow-down.svg";
import { ReactComponent as FileWarning } from "@assets/images/icons/file-warning.svg";

import "./CopytradeTradersList.sass";

// --- SORT ICON COMPONENT ---
// Using inline SVGs here so you don't have to worry about importing multiple arrows
const SortIcon = ({ direction }) => (
  <div className="followers-table__sort-icon">
    <ArrowDownIcon
      className={`followers-table__sort-arrow followers-table__sort-arrow--up ${
        direction === "asc" ? "active" : ""
      }`}
    />

    <ArrowDownIcon
      className={`followers-table__sort-arrow followers-table__sort-arrow--down ${
        direction === "desc" ? "active" : ""
      }`}
    />
  </div>
);

export const TradersList = ({ data }) => {
  // --- ALL TRADERS FILTERS STATE ---
  const [period, setPeriod] = useState("7d");
  const [rank, setRank] = useState("Master Trader Rank");
  const [type, setType] = useState("Master Trader Type");
  const [country, setCountry] = useState("Country");
  const [badge, setBadge] = useState("Master Trader Badge");
  const [isAvailableOnly, setIsAvailableOnly] = useState(false);

  // --- SORTING STATE ---
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") direction = "desc";
      else if (sortConfig.direction === "desc") direction = null; // Reset state
    }
    setSortConfig({ key, direction });
  };

  const getSortDir = (key) =>
    sortConfig.key === key ? sortConfig.direction : null;

  const sortedData = useMemo(() => {
    if (!sortConfig.direction || !sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      if (valA === undefined || valB === undefined) return 0;

      const numA = parseFloat(String(valA).replace(/[^0-9.-]+/g, ""));
      const numB = parseFloat(String(valB).replace(/[^0-9.-]+/g, ""));

      if (numA < numB) return sortConfig.direction === "asc" ? -1 : 1;
      if (numA > numB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  return (
    <>
      <div className="copytraders-filters">
        <BaseDropdown
          trigger={
            <button className="copytraders-filters__btn">
              {period}
              <ArrowIcon />
            </button>
          }
        >
          {({ closeDropdown }) => (
            <div className="copytraders-filters__menu">
              {["7d", "30d", "90d"].map((opt) => (
                <div
                  key={opt}
                  className="copytraders-filters__menu-item"
                  onClick={() => {
                    setPeriod(opt);
                    closeDropdown();
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </BaseDropdown>

        <BaseDropdown
          trigger={
            <button className="copytraders-filters__btn">
              {rank} <ArrowIcon />
            </button>
          }
        >
          {({ closeDropdown }) => (
            <div className="copytraders-filters__menu">
              {["All Ranks", "Gold", "Silver", "Bronze"].map((opt) => (
                <div
                  key={opt}
                  className="copytraders-filters__menu-item"
                  onClick={() => {
                    setRank(opt);
                    closeDropdown();
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </BaseDropdown>

        <BaseDropdown
          trigger={
            <button className="copytraders-filters__btn">
              {type} <ArrowIcon />
            </button>
          }
        >
          {({ closeDropdown }) => (
            <div className="copytraders-filters__menu">
              {["All Types", "Spot", "Futures"].map((opt) => (
                <div
                  key={opt}
                  className="copytraders-filters__menu-item"
                  onClick={() => {
                    setType(opt);
                    closeDropdown();
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </BaseDropdown>

        <BaseDropdown
          trigger={
            <button className="copytraders-filters__btn">
              {country} <ArrowIcon />
            </button>
          }
        >
          {({ closeDropdown }) => (
            <div className="copytraders-filters__menu">
              {["All Countries", "Local", "Global"].map((opt) => (
                <div
                  key={opt}
                  className="copytraders-filters__menu-item"
                  onClick={() => {
                    setCountry(opt);
                    closeDropdown();
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </BaseDropdown>

        <BaseDropdown
          trigger={
            <button className="copytraders-filters__btn">
              {badge} <ArrowIcon />
            </button>
          }
        >
          {({ closeDropdown }) => (
            <div className="copytraders-filters__menu">
              {["All Badges", "Verified", "Top Gainer"].map((opt) => (
                <div
                  key={opt}
                  className="copytraders-filters__menu-item"
                  onClick={() => {
                    setBadge(opt);
                    closeDropdown();
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </BaseDropdown>

        {/* Checkbox for Available Traders */}
        <input
          type="checkbox"
          id="cp"
          className="bo-checkbox2 copytraders-filters__checkbox"
          checked={isAvailableOnly}
          onChange={() => setIsAvailableOnly((prev) => !prev)}
          value={isAvailableOnly}
        />
        <label
          htmlFor="cp"
          className="bo-checkbox2-label copytraders-filters__checkbox-label"
        >
          <p>Available Master Traders</p>
        </label>
      </div>

      <div className="copytraders-list">
        <table className="copytraders-list__table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("name")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">Nickname</div>
              </th>
              <th
                onClick={() => handleSort("pnlPercent")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d ROI <SortIcon direction={getSortDir("pnlPercent")} />
                </div>
              </th>
              <th
                onClick={() => handleSort("drawdown")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d Drawdown <SortIcon direction={getSortDir("drawdown")} />
                </div>
              </th>
              <th
                onClick={() => handleSort("pnlValue")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d Followers’ PnL{" "}
                  <SortIcon direction={getSortDir("pnlValue")} />
                </div>
              </th>
              <th
                onClick={() => handleSort("winRate")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d Win Rate <SortIcon direction={getSortDir("winRate")} />
                </div>
              </th>
              <th
                onClick={() => handleSort("plRatio")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d P/L Ratio <SortIcon direction={getSortDir("plRatio")} />
                </div>
              </th>
              <th
                onClick={() => handleSort("sharpeRatio")}
                className="copytraders-list__sortable-th"
              >
                <div className="copytraders-list__flex-wrap">
                  7d Sharpe Ratio{" "}
                  <SortIcon direction={getSortDir("sharpeRatio")} />
                </div>
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {sortedData.map((trader, i) => {
              const isNegative = trader.pnlPercent.startsWith("-");
              return (
                <tr key={i}>
                  <td className="copytraders-list__cell">
                    <div className="copytraders-list__cell-wrapper">
                      <div
                        className={`copytrader-card__profile-star ${
                          trader.isStarred &&
                          "copytrader-card__profile-star--active"
                        }`}
                      >
                        <StarIcon />
                      </div>{" "}
                      {trader.name}
                    </div>
                  </td>
                  <td
                    className={`copytraders-list__cell ${
                      isNegative
                        ? "copytraders-list__cell--negative"
                        : "copytraders-list__cell--positive"
                    }`}
                  >
                    <div className="copytraders-list__value-wrapper">
                      {isNegative ? <ArrowDownRed /> : <ArrowUpGreen />}
                      <span>{trader.pnlPercent}</span>
                    </div>
                  </td>
                  <td className="copytraders-list__cell">12.4%</td>{" "}
                  {/* Mock data: add real property if you have it */}
                  <td className="copytraders-list__cell">{trader.pnlValue}</td>
                  <td className="copytraders-list__cell">68%</td>{" "}
                  {/* Mock data */}
                  <td className="copytraders-list__cell">1.45</td>{" "}
                  {/* Mock data */}
                  <td
                    className={`copytraders-list__cell ${
                      isNegative
                        ? "copytraders-list__cell--negative"
                        : "copytraders-list__cell--positive"
                    }`}
                  >
                    <div className="copytraders-list__value-wrapper">
                      {isNegative ? <ArrowDownRed /> : <ArrowUpGreen />}
                      <span>--</span>
                    </div>
                  </td>{" "}
                  {/* Mock data */}
                  <td className="copytraders-list__cell">
                    <button className="copytraders-list__action-btn">
                      Copy
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.length === 0 && (
          <p className="copytraders-list__empty">
            <FileWarning />
            No Results Found.
          </p>
        )}
      </div>
    </>
  );
};
